import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useJwt} from '../../../helpers/common';
import {useOrganizationService} from '../../../services';
import { setOrganizationOnboardSettings } from '../../../redux/slices/organization';
import {
  OnboardingStepKeyEnum,
  PMTOOLS_PROVIDER_ORDER,
  RISK_MITIGATION_SUBSTEPS,
  type UseOnboardingStepOptions,
  type UseOnboardingStepReturn
} from '../types';

export function useOnboardingStep(stepKey: string, options?: UseOnboardingStepOptions): UseOnboardingStepReturn {
  const { onboardSettings = {}, onboardSettingsLoaded } = useSelector((state: any) => state.organization);
  const dispatch = useDispatch();
  const JWTdata: any = useJwt();
  const orgId = JWTdata?.org_id;
  const isAdmin = typeof JWTdata?.org_role === 'string' && String(JWTdata?.org_role).toLowerCase() === 'admin';
  const { updateOrganization } = useOrganizationService();

  const isPmTools = stepKey === OnboardingStepKeyEnum.PmTools;
  const isRiskMitigation = stepKey === OnboardingStepKeyEnum.RiskMitigation;
  const isOrgSkills = stepKey === OnboardingStepKeyEnum.OrgSkills;

  const isCompleted = useMemo(() => {
    if (isPmTools) {
      const pm = onboardSettings?.pm_tools || {};
      return PMTOOLS_PROVIDER_ORDER.every(k => pm?.[k]);
    }
    // if (isOrgSkills) {
    //   const pm = onboardSettings?.pm_tools || {};
    //   return PMTOOLS_PROVIDER_ORDER.every(k => pm?.[k]);
    // }
    if (isRiskMitigation) {
      const risk = onboardSettings?.risk_mitigation || {};
      return RISK_MITIGATION_SUBSTEPS.every(k => !!risk?.[k]);
    }
    return !!onboardSettings?.[stepKey];
  }, [isPmTools, isRiskMitigation, onboardSettings, stepKey]);

  const show = useMemo(() => {
    if (!isAdmin) return false; // Only admins see onboarding
    if (onboardSettings?.close) return false; // global gate
    if (onboardSettings?.dismissed === true) return false; // globally dismissed tooltips
    if (isCompleted) return false;
    if (!onboardSettingsLoaded) return false; // suppress until onboard settings are loaded to avoid flicker
    if (typeof options?.shouldShow === 'boolean') {
      return options.shouldShow;
    }
    return true;
  }, [isAdmin, isCompleted, options?.shouldShow, onboardSettings?.close, onboardSettings?.dismissed, onboardSettingsLoaded]);

  const complete = useCallback(async () => {
    if (!orgId) return;
    try {
      const current: any = onboardSettings || {};
      const next: any = { ...current };
      let changed = false;

      let onlyLocalChanges = false;
      if (stepKey === OnboardingStepKeyEnum.PmTools) {
        const currentPm: Record<string, boolean> = current?.pm_tools || {};
        const nextPm: Record<string, boolean> = { ...currentPm };

        if (options?.providerKey) {
          if (!nextPm[options.providerKey]) {
            nextPm[options.providerKey] = true;
            changed = true;
          }
          const lastKey = PMTOOLS_PROVIDER_ORDER[PMTOOLS_PROVIDER_ORDER.length - 1];
          if (options.providerKey !== lastKey) {
            onlyLocalChanges = true;
          }
        } else {
          for (const k of PMTOOLS_PROVIDER_ORDER) {
            if (!nextPm[k]) {
              nextPm[k] = true;
              changed = true;
            }
          }
        }

        if (changed) next.pm_tools = nextPm;
      } else if (stepKey === OnboardingStepKeyEnum.RiskMitigation) {
        const currentRisk: Record<string, boolean> = current?.risk_mitigation || {};
        const nextRisk: Record<string, boolean> = { ...currentRisk };

        if (options?.riskSubKey) {
          if (!nextRisk[options.riskSubKey]) {
            nextRisk[options.riskSubKey] = true;
            changed = true;
          }
        } else {
          for (const k of RISK_MITIGATION_SUBSTEPS) {
            if (!nextRisk[k]) {
              nextRisk[k] = true;
              changed = true;
            }
          }
        }

        if (changed) next.risk_mitigation = nextRisk;
      } else {
        if (!current?.[stepKey]) {
          next[stepKey] = true;
          changed = true;
        }
      }

      if (!changed) return;

      next.current_navigation = null;

      dispatch(setOrganizationOnboardSettings(next));

      if (!onlyLocalChanges) await updateOrganization(orgId, { onboard_settings: next }, true);
    } catch (e) {}
  }, [orgId, onboardSettings, stepKey, options?.providerKey, options?.riskSubKey, updateOrganization, dispatch]);

  return { show, complete, isCompleted };
} 